/**
 * Dialog para importação de fluxos
 * Layout 2 colunas: sidebar esquerda com navegação + conteúdo scrollável direita
 * Segue o padrão visual do CreateCampaignDialog
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Implementação inicial
 * RELEASE: 1.1.0 - 2026-02-03 - Fase 4: Wizard de Mapeamento
 * RELEASE: 2.0.0 - 2026-02-03 - Refatoração para layout 2 colunas
 */

import { LegacyTransferNotice } from '@/components/template-system/LegacyTransferNotice';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  Upload,
  FileJson,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader2,
  User,
  Building2,
  Inbox,
  Tag,
  Briefcase,
  Layers,
  Key,
  MessageSquare,
  FileWarning,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Variable,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';
import { useAuth } from '@/hooks/useAuth';
import {
  parseImportFile,
  importFlow,
  autoMatchReferences,
  type ImportValidationResult,
} from '@/lib/flows/flow-import';
import {
  REFERENCE_TYPE_CONFIGS,
  type ExternalReference,
  type ExternalReferenceType,
  type ReferenceMapping,
  type AutoMatchResult,
} from '@/lib/flows/reference-types';
import { ReferenceMappingContent } from './import/ReferenceMappingWizard';
import type { FlowExportFormat } from '@/types/flows';
import type { LucideIcon } from 'lucide-react';
import { SellerFormDialog } from '@/components/crm/sellers/SellerFormDialog';
import { CreateDepartmentDialog } from '@/components/tenant/departments/CreateDepartmentDialog';
import { CreateInboxDialog } from '@/components/tenant/CreateInboxDialog';
import { CreateCredentialDialog } from '@/components/tenant/credentials/CreateCredentialDialog';
import { CreateMeioPagamentoDialog } from '@/components/financial/CreateMeioPagamentoDialog';

/** Tipos que suportam criação inline via modais existentes */
const CREATABLE_TYPES: ExternalReferenceType[] = ['seller', 'department', 'inbox', 'credential', 'meio_pagamento'];

/** Logger para o componente */
const log = createLogger('ImportFlowDialog');

/** Mapeamento de ícones por tipo de referência */
const REFERENCE_ICONS: Partial<Record<ExternalReferenceType, LucideIcon>> = {
  agent: User,
  department: Building2,
  inbox: Inbox,
  contact_tag: Tag,
  deal_tag: Tag,
  workspace: Briefcase,
  pipeline: Layers,
  phase: Layers,
  kanban_category: Layers,
  kanban_phase: Layers,
  distribution_queue: User,
  credential: Key,
  template: MessageSquare,
  seller: User,
  tenant_variable: Variable,
};

/** Configuração dos steps do wizard */
interface StepConfig {
  id: number;
  key: 'upload' | 'preview' | 'mapping';
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  description: string;
}

const STEPS: StepConfig[] = [
  { 
    id: 1, 
    key: 'upload',
    label: 'Upload', 
    shortLabel: 'Upload',
    icon: Upload,
    description: 'Selecione o arquivo .uaizyflow para importar' 
  },
  { 
    id: 2, 
    key: 'preview',
    label: 'Preview', 
    shortLabel: 'Preview',
    icon: FileText,
    description: 'Revise o fluxo e referências externas' 
  },
  { 
    id: 3, 
    key: 'mapping',
    label: 'Mapeamento', 
    shortLabel: 'Mapear',
    icon: Layers,
    description: 'Mapeie as entidades externas para o seu tenant' 
  },
];

interface ImportFlowDialogProps {
  /** Estado de abertura do dialog */
  open: boolean;
  /** Callback para mudança de estado */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog de importação de fluxos com layout 2 colunas
 * Permite upload de arquivo, preview e mapeamento de referências
 */
export function ImportFlowDialog({
  open,
  onOpenChange,
}: ImportFlowDialogProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // =========================================================================
  // Estados do dialog
  // =========================================================================
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [parseResult, setParseResult] = useState<ImportValidationResult | null>(null);
  const [customName, setCustomName] = useState('');

  // Estados do mapeamento
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [autoMatches, setAutoMatches] = useState<AutoMatchResult[]>([]);
  const [mappings, setMappings] = useState<Map<string, string | null>>(new Map());
  const [currentMappingType, setCurrentMappingType] = useState(0);
  
  // Estado para criação automática de variáveis
  const [autoCreateVariables, setAutoCreateVariables] = useState(false);

  // Estado do modal de criação inline (botão "+")
  const [createDialogType, setCreateDialogType] = useState<ExternalReferenceType | null>(null);

  // =========================================================================
  // Computações derivadas
  // =========================================================================
  
  const totalSteps = STEPS.length;
  const currentStepConfig = STEPS[step - 1];
  const StepIcon = currentStepConfig.icon;
  const hasReferences = (parseResult?.references?.length || 0) > 0;
  const hasErrors = (parseResult?.errors?.length || 0) > 0;
  const canImport = parseResult?.data && parseResult.valid && customName.trim().length > 0;

  // Agrupa referências por tipo
  const referencesByType = useMemo(() => {
    if (!parseResult?.references) return new Map<ExternalReferenceType, ExternalReference[]>();
    const grouped = new Map<ExternalReferenceType, ExternalReference[]>();
    for (const ref of parseResult.references) {
      if (!grouped.has(ref.type)) {
        grouped.set(ref.type, []);
      }
      grouped.get(ref.type)!.push(ref);
    }
    return grouped;
  }, [parseResult?.references]);

  // Lista de tipos para navegação
  const mappingTypes = useMemo(() => Array.from(referencesByType.keys()), [referencesByType]);

  // Estatísticas de mapeamento
  const mappingStats = useMemo(() => {
    if (!parseResult?.references) return { total: 0, mapped: 0, required: 0, requiredMapped: 0, canComplete: true };
    
    let mapped = 0;
    let required = 0;
    let requiredMapped = 0;

    for (const ref of parseResult.references) {
      const config = REFERENCE_TYPE_CONFIGS[ref.type];
      const isMapped = mappings.has(ref.placeholder);
      
      if (isMapped) mapped++;
      if (config?.required) {
        required++;
        if (isMapped) requiredMapped++;
      }
    }

    return {
      total: parseResult.references.length,
      mapped,
      required,
      requiredMapped,
      canComplete: requiredMapped === required,
    };
  }, [parseResult?.references, mappings]);

  const mappingProgress = mappingStats.total > 0 ? (mappingStats.mapped / mappingStats.total) * 100 : 0;

  // =========================================================================
  // Handlers
  // =========================================================================

  /**
   * Reseta o estado do dialog
   */
  const resetState = useCallback(() => {
    setStep(1);
    setFile(null);
    setParseResult(null);
    setCustomName('');
    setIsParsing(false);
    setIsImporting(false);
    setIsLoadingMatches(false);
    setAutoMatches([]);
    setCreateDialogType(null);
    setMappings(new Map());
    setCurrentMappingType(0);
    setAutoCreateVariables(false);
  }, []);

  /**
   * Handler para quando o dialog fecha
   */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  /**
   * Handler para drag over
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  /**
   * Handler para drag leave
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  /**
   * Handler para drop
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handler para seleção de arquivo via input
   */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Processa o arquivo selecionado
   */
  const processFile = async (selectedFile: File) => {
    // Validar extensão
    const validExtensions = ['.json', '.uaizyflow'];
    const hasValidExtension = validExtensions.some(ext => 
      selectedFile.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExtension) {
      toast({
        variant: 'destructive',
        title: 'Arquivo inválido',
        description: 'Por favor, selecione um arquivo .json ou .uaizyflow',
      });
      return;
    }

    // Validar tamanho (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      toast({
        variant: 'destructive',
        title: 'Arquivo muito grande',
        description: 'O arquivo deve ter no máximo 10MB',
      });
      return;
    }

    setFile(selectedFile);
    setIsParsing(true);

    try {
      log.info('Parsing arquivo de importação', { fileName: selectedFile.name });
      const result = await parseImportFile(selectedFile);
      setParseResult(result);
      
      // Definir nome sugerido
      if (result.data) {
        setCustomName(result.data.flow.name + ' (importado)');
      }

      if (result.errors.length > 0) {
        log.warn('Erros encontrados no parsing', { errors: result.errors });
      }

      // Avançar para preview se válido
      if (result.valid) {
        setStep(2);
      }
    } catch (error) {
      log.error('Erro ao parsear arquivo', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao processar arquivo',
        description: error instanceof Error ? error.message : 'Arquivo inválido',
      });
      setFile(null);
    } finally {
      setIsParsing(false);
    }
  };

  /**
   * Remove o arquivo selecionado
   */
  const clearFile = () => {
    setFile(null);
    setParseResult(null);
    setCustomName('');
    setStep(1);
  };

  /**
   * Navega para o próximo step
   */
  const handleNext = () => {
    if (step < totalSteps) {
      // Ao ir para mapeamento, carregar auto-matches
      if (step === 2 && hasReferences) {
        loadAutoMatches();
      }
      setStep(s => s + 1);
    }
  };

  /**
   * Navega para o step anterior
   */
  const handleBack = () => {
    if (step > 1) {
      setStep(s => s - 1);
    }
  };

  /**
   * Carrega auto-matches para o step de mapeamento
   */
  const loadAutoMatches = async () => {
    if (!parseResult?.references || !currentTenant?.id) return;

    setIsLoadingMatches(true);
    try {
      log.info('Carregando auto-matches', { 
        tenantId: currentTenant.id, 
        referenceCount: parseResult.references.length,
      });

      const matches = await autoMatchReferences(parseResult.references, currentTenant.id);
      setAutoMatches(matches);

      // Aplicar matches exatos automaticamente
      const initialMappings = new Map<string, string | null>();
      for (const match of matches) {
        const exactCandidate = match.candidates.find(c => c.similarity === 1);
        if (exactCandidate) {
          initialMappings.set(match.reference.placeholder, exactCandidate.id);
          log.info('Auto-match exato aplicado', {
            placeholder: match.reference.placeholder,
            mappedTo: exactCandidate.name,
          });
        }
      }
      setMappings(initialMappings);

      log.info('Auto-matches carregados', {
        totalMatches: matches.length,
        autoMapped: initialMappings.size,
      });
    } catch (error) {
      log.error('Erro ao carregar auto-matches', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar sugestões',
        description: 'Não foi possível buscar sugestões de mapeamento',
      });
    } finally {
      setIsLoadingMatches(false);
    }
  };

  /**
   * Handler para mudança de mapeamento
   */
  const handleMappingChange = useCallback((placeholder: string, mappedId: string | null) => {
    setMappings(prev => {
      const next = new Map(prev);
      if (mappedId === null) {
        next.delete(placeholder);
      } else {
        next.set(placeholder, mappedId);
      }
      return next;
    });
  }, []);

  /**
   * Abre o modal de criação para o tipo de entidade especificado
   * Chamado pelo botão "+" no ReferenceMappingItem
   */
  const handleCreateForType = useCallback((type: ExternalReferenceType) => {
    if (CREATABLE_TYPES.includes(type)) {
      setCreateDialogType(type);
    }
  }, []);

  /**
   * Callback de sucesso após criar entidade inline
   * Recarrega auto-matches para atualizar candidatos disponíveis
   */
  const handleCreateSuccess = useCallback(async () => {
    setCreateDialogType(null);
    if (!currentTenant?.id || !parseResult?.references) return;

    try {
      const matches = await autoMatchReferences(parseResult.references, currentTenant.id);
      setAutoMatches(matches);

      // Reaplicar matches exatos
      const updated = new Map(mappings);
      for (const match of matches) {
        const exact = match.candidates.find(c => c.similarity === 1);
        if (exact && !updated.has(match.reference.placeholder)) {
          updated.set(match.reference.placeholder, exact.id);
        }
      }
      setMappings(updated);
      log.info('Candidatos atualizados após criação inline');
    } catch (err) {
      log.error('Erro ao recarregar candidatos', err);
    }
  }, [currentTenant?.id, parseResult?.references, mappings]);

  /**
   * Importa o fluxo sem mapeamento (para fluxos sem referências)
   */
  const handleImportDirect = async () => {
    if (!parseResult?.data || !currentTenant?.id) return;

    setIsImporting(true);

    try {
      log.info('Importando fluxo diretamente (sem referências)', { 
        flowName: customName,
        tenantId: currentTenant.id,
      });

      const result = await importFlow(
        parseResult.data,
        currentTenant.id,
        [],
        customName
      );

      log.info('Fluxo importado com sucesso', { newFlowId: result.flowId });

      toast({
        title: 'Fluxo importado',
        description: `Fluxo "${customName}" importado com sucesso.`,
      });

      queryClient.invalidateQueries({ queryKey: ['flows'] });
      handleOpenChange(false);
    } catch (error) {
      log.error('Erro ao importar fluxo', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao importar',
        description: error instanceof Error ? error.message : 'Não foi possível importar o fluxo',
      });
    } finally {
      setIsImporting(false);
    }
  };

  /**
   * Importa o fluxo com mapeamentos
   */
  const handleImportWithMappings = async () => {
    if (!parseResult?.data || !currentTenant?.id || !parseResult.references) return;

    setIsImporting(true);

    try {
      // Converter mapeamentos para formato esperado
      const mappingsResult: ReferenceMapping[] = parseResult.references.map(ref => {
        const mappedId = mappings.get(ref.placeholder) || null;
        const autoMatch = autoMatches.find(m => m.reference.placeholder === ref.placeholder);
        const matchedCandidate = autoMatch?.candidates.find(c => c.id === mappedId);

        return {
          placeholder: ref.placeholder,
          mappedId,
          mappedName: matchedCandidate?.name,
          autoMatched: matchedCandidate?.similarity === 1,
        };
      });

      log.info('Importando fluxo com mapeamentos', { 
        flowName: customName,
        tenantId: currentTenant.id,
        mappingCount: mappingsResult.length,
        mappedCount: mappingsResult.filter(m => m.mappedId).length,
      });

      const result = await importFlow(
        parseResult.data,
        currentTenant.id,
        mappingsResult,
        customName,
        { autoCreateVariables }
      );

      log.info('Fluxo importado com sucesso', { 
        newFlowId: result.flowId,
        unmappedCount: result.unmappedReferences.length,
      });

      // Separar warnings de triggers dos demais para mensagem mais específica
      const triggerWarnings = result.unmappedReferences.filter(
        r => r.context?.includes('Gatilho')
      );
      const nodeWarnings = result.unmappedReferences.filter(
        r => !r.context?.includes('Gatilho')
      );

      // Toast de sucesso com detalhes apropriados
      if (triggerWarnings.length > 0 || nodeWarnings.length > 0) {
        const parts: string[] = [];
        
        if (triggerWarnings.length > 0) {
          parts.push(`${triggerWarnings.length} campo${triggerWarnings.length !== 1 ? 's' : ''} do gatilho precisa${triggerWarnings.length === 1 ? '' : 'm'} ser configurado${triggerWarnings.length !== 1 ? 's' : ''}`);
        }
        
        if (nodeWarnings.length > 0) {
          parts.push(`${nodeWarnings.length} referência${nodeWarnings.length !== 1 ? 's' : ''} de node${nodeWarnings.length !== 1 ? 's' : ''} não mapeada${nodeWarnings.length !== 1 ? 's' : ''}`);
        }

        toast({
          title: 'Fluxo importado com pendências',
          description: `Fluxo "${customName}" criado como rascunho. ${parts.join('. ')}.`,
        });
      } else {
        toast({
          title: 'Fluxo importado',
          description: `Fluxo "${customName}" importado com sucesso.`,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['flows'] });
      handleOpenChange(false);
    } catch (error) {
      log.error('Erro ao importar fluxo', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao importar',
        description: error instanceof Error ? error.message : 'Não foi possível importar o fluxo',
      });
    } finally {
      setIsImporting(false);
    }
  };

  /**
   * Verifica se o step atual está válido
   */
  const isStepValid = (): boolean => {
    switch (step) {
      case 1:
        return !!file && !!parseResult?.valid;
      case 2:
        return canImport || false;
      case 3:
        return mappingStats.canComplete;
      default:
        return true;
    }
  };

  // =========================================================================
  // Renderização de conteúdo por step
  // =========================================================================

  const renderStepContent = () => {
    switch (step) {
      // ===== STEP 1: UPLOAD =====
      case 1:
        return (
          <div className="space-y-4">
            {/* Área de upload */}
            {!file && (
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                  isDragging 
                    ? "border-primary bg-primary/5" 
                    : "border-muted-foreground/25 hover:border-primary/50"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="flow-file-input"
                  accept=".json,.uaizyflow"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="flow-file-input"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10">
                    <FileJson className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">
                      Arraste um arquivo ou clique para selecionar
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Formatos aceitos: .json, .uaizyflow (máx. 10MB)
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Arquivo selecionado - Loading */}
            {file && isParsing && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-sm text-muted-foreground">Analisando arquivo...</p>
                </div>
              </div>
            )}

            {/* Erros de parsing */}
            {file && !isParsing && hasErrors && parseResult?.errors && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
                  <FileJson className="h-5 w-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={clearFile}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                  <FileWarning className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm text-destructive">
                      Erros encontrados no arquivo
                    </p>
                    <ul className="text-xs text-destructive/80 mt-1 list-disc list-inside">
                      {parseResult.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      // ===== STEP 2: PREVIEW =====
      case 2:
        if (!parseResult?.data) return null;
        
        return (
          <div className="space-y-4">
            {/* Info do arquivo */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
              <FileJson className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{file?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {file ? (file.size / 1024).toFixed(1) : 0} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={clearFile}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Nome customizável */}
            <div className="space-y-2">
              <Label htmlFor="flow-name">Nome do fluxo</Label>
              <Input
                id="flow-name"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Nome do fluxo importado"
              />
            </div>

            {/* Info do fluxo original */}
            <div className="p-3 rounded-lg bg-muted/30 border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Descrição:</span>
                <span className="text-sm font-medium truncate max-w-[200px]">
                  {parseResult.data.flow.description || 'Sem descrição'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Blocos:</span>
                <Badge variant="secondary">
                  {parseResult.data.flow.nodes?.length || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Versão do formato:</span>
                <span className="text-xs font-mono">
                  {parseResult.data.format_version}
                </span>
              </div>
            </div>

            {/* Referências externas */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border">
              {hasReferences && parseResult.references ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {parseResult.references.length} referência{parseResult.references.length !== 1 ? 's' : ''} externa{parseResult.references.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Você poderá mapear estas referências na próxima etapa.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm">
                      Nenhuma referência externa
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Este fluxo está pronto para uso imediato.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Lista de referências agrupadas (preview) */}
            {hasReferences && parseResult?.references && (
              <div className="space-y-3 max-h-40 overflow-y-auto pr-2">
                {Array.from(referencesByType.entries()).map(([type, refs]) => {
                  const config = REFERENCE_TYPE_CONFIGS[type];
                  const Icon = REFERENCE_ICONS[type] || Tag;
                  
                  return (
                    <div key={type} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">
                          {config?.label || type}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {refs.length}
                        </Badge>
                      </div>
                      <div className="pl-6 space-y-0.5">
                        {refs.slice(0, 3).map((ref, idx) => (
                          <div
                            key={`${ref.placeholder}-${idx}`}
                            className="text-xs text-muted-foreground flex items-center gap-2"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                            <span className="font-medium text-foreground">
                              {ref.name}
                            </span>
                          </div>
                        ))}
                        {refs.length > 3 && (
                          <p className="text-xs text-muted-foreground pl-3.5">
                            +{refs.length - 3} mais...
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Separator />

            {/* Info sobre importação */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• O fluxo será criado com status <strong>Rascunho</strong></p>
              {hasReferences && (
                <p>• Referências não mapeadas ficarão com placeholders no fluxo</p>
              )}
            </div>
          </div>
        );

      // ===== STEP 3: MAPEAMENTO =====
      case 3:
        if (isLoadingMatches) {
          return (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Analisando referências...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Buscando correspondências no seu tenant
                </p>
              </div>
            </div>
          );
        }

        if (!parseResult?.references || mappingTypes.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="text-muted-foreground">Nenhuma referência para mapear</p>
            </div>
          );
        }

        return (
          <ReferenceMappingContent
            references={parseResult.references}
            referencesByType={referencesByType}
            mappingTypes={mappingTypes}
            currentMappingType={currentMappingType}
            onMappingTypeChange={setCurrentMappingType}
            mappings={mappings}
            onMappingChange={handleMappingChange}
            autoMatches={autoMatches}
            mappingStats={mappingStats}
            mappingProgress={mappingProgress}
            autoCreateVariables={autoCreateVariables}
            onAutoCreateVariablesChange={setAutoCreateVariables}
            onCreateForType={handleCreateForType}
          />
        );

      default:
        return null;
    }
  };

  // =========================================================================
  // Renderização Principal
  // =========================================================================

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-5xl max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0 flex flex-col"
        preventCloseOnOutsideInteraction={step === 3}
      >
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          {/* ============================================================ */}
          {/* SIDEBAR - Navegação de Steps + Resumo */}
          {/* ============================================================ */}
          <aside 
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Header da Sidebar */}
            <div className="relative z-10 p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Upload className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Importar Fluxo</h2>
                  <p className="text-xs text-white/60">
                    {step} de {totalSteps} etapas
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de Steps */}
            <nav className="relative z-10 flex-1 p-4 space-y-1 overflow-y-auto">
              {STEPS.map((s) => {
                const isActive = step === s.id;
                const isCompleted = step > s.id;
                const Icon = s.icon;

                // Condições para habilitar click
                const canClick = s.id < step || (s.id === 2 && file && parseResult?.valid) || (s.id === 3 && hasReferences && step >= 2);

                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      if (canClick) setStep(s.id);
                    }}
                    disabled={!canClick}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all",
                      "hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed",
                      isActive && "bg-white/10 border border-white/20",
                      !isActive && !isCompleted && "text-white/50"
                    )}
                  >
                    {/* Círculo indicador */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      "border-2 transition-colors",
                      isCompleted && "bg-green-500 border-green-500 text-white",
                      isActive && "bg-white border-white text-primary",
                      !isActive && !isCompleted && "border-white/30 text-white/50"
                    )}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>

                    {/* Texto do step */}
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        "font-medium text-sm block truncate",
                        isActive && "text-white",
                        isCompleted && "text-white/70",
                        !isActive && !isCompleted && "text-white/50"
                      )}>
                        {s.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Resumo em tempo real */}
            <div className="relative z-10 p-4 border-t border-white/10 bg-white/5">
              <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                Resumo
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Arquivo</span>
                  <span className="font-medium text-white truncate max-w-[120px]">
                    {file?.name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Blocos</span>
                  <span className="font-medium text-white">
                    {parseResult?.data?.flow?.nodes?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Referências</span>
                  <span className="font-medium text-white">
                    {parseResult?.references?.length || 0}
                  </span>
                </div>
                {step === 3 && mappingStats.total > 0 && (
                  <div className="flex justify-between">
                    <span className="text-white/60">Mapeadas</span>
                    <span className="font-medium text-white">
                      {mappingStats.mapped}/{mappingStats.total}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* ============================================================ */}
          {/* CONTEÚDO PRINCIPAL */}
          {/* ============================================================ */}
          <main className="flex flex-col h-full min-h-0 overflow-hidden">
            {/* Header do Step */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStepConfig.label}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStepConfig.description}
                  </DialogDescription>
                </div>
                {/* Badge mobile mostrando progresso */}
                <Badge variant="outline" className="md:hidden">
                  {step}/{totalSteps}
                </Badge>
              </div>

              {/* Stepper mobile */}
              <div className="flex items-center gap-1.5 mt-4 md:hidden">
                {STEPS.map((s) => (
                  <div
                    key={s.id}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors",
                      step > s.id && "bg-green-500",
                      step === s.id && "bg-primary",
                      step < s.id && "bg-muted"
                    )}
                  />
                ))}
              </div>
            </DialogHeader>

            {/* Aviso do período de compatibilidade do formato antigo (.uaizyflow) */}
            <div className="px-6 pt-4">
              <LegacyTransferNotice mode="import" />
            </div>



            {/* Área de Conteúdo - Step 3 usa layout especial para contenção */}
            {step === 3 ? (
              // Step 3: Container flex que propaga altura para ReferenceMappingContent
              <div className="flex-1 min-h-0 flex flex-col px-6 py-5 overflow-hidden">
                {renderStepContent()}
              </div>
            ) : (
              // Outros steps: Container com scroll normal
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="px-6 py-5">
                  {renderStepContent()}
                </div>
              </div>
            )}

            {/* Footer com Navegação - Layout corrigido para garantir visibilidade dos botões */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-background flex items-center gap-4 sticky bottom-0">
              {/* Botão Voltar - shrink-0 impede compressão */}
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={step === 1}
                className="shrink-0 gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>

              {/* Texto central - flex-1 para ocupar espaço disponível */}
              <span className="flex-1 text-center text-sm text-muted-foreground hidden sm:block">
                Etapa {step} de {totalSteps}
              </span>

              {/* Botões de ação baseado no step - shrink-0 impede compressão */}
              {step === 1 && (
                <Button disabled className="shrink-0 gap-1">
                  Próximo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}

              {step === 2 && (
                <>
                  {hasReferences ? (
                    <Button
                      onClick={handleNext}
                      disabled={!canImport}
                      className="shrink-0 gap-1"
                    >
                      Próximo
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleImportDirect} 
                      disabled={!canImport || isImporting}
                      className="shrink-0 gap-2"
                    >
                      {isImporting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Importando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Importar
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}

              {step === 3 && (
                <Button 
                  onClick={handleImportWithMappings}
                  disabled={!mappingStats.canComplete || isImporting}
                  className="shrink-0 gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Concluir importação
                    </>
                  )}
                </Button>
              )}
            </div>
          </main>
        </div>
      </DialogContent>

      {/* Modais de criação inline para entidades referenciadas */}

      {createDialogType === 'seller' && (
        <SellerFormDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          onSuccess={handleCreateSuccess}
        />
      )}

      {createDialogType === 'department' && currentTenant?.id && (
        <CreateDepartmentDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          tenantId={currentTenant.id}
          onSuccess={handleCreateSuccess}
        />
      )}

      {createDialogType === 'inbox' && currentTenant?.id && (
        <CreateInboxDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          tenantId={currentTenant.id}
          onSuccess={handleCreateSuccess}
        />
      )}

      {createDialogType === 'credential' && currentTenant?.id && (
        <CreateCredentialDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          tenantId={currentTenant.id}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Modal de criação de meio de pagamento */}
      {createDialogType === 'meio_pagamento' && (
        <CreateMeioPagamentoDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          onSuccess={handleCreateSuccess}
        />
      )}
    </Dialog>
  );
}
